import React, { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const SEVERITY_COLORS: Record<string, string> = {
  'Near Miss': '#3b82f6',
  'Minor Injury': '#f59e0b',
  'Lost Time Injury': '#f97316',
  'Fatality': '#dc2626',
}

const STATUS_COLORS: Record<string, string> = {
  'Open': '#dc2626',
  'Under Review': '#f59e0b',
  'Closed': '#16a34a',
}

const STATUS_BG: Record<string, string> = {
  'Open': '#fef2f2',
  'Under Review': '#fffbeb',
  'Closed': '#f0fdf4',
}

const SEVERITY_ORDER = ['Near Miss', 'Minor Injury', 'Lost Time Injury', 'Fatality']
const STATUS_ORDER = ['Open', 'Under Review', 'Closed']

export default function IncidentsPage() {
  const { orgId, isAdmin, hasFeature } = useAuth()
  const [incidents, setIncidents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('All')
  const [filterSeverity, setFilterSeverity] = useState<string>('All')
  const [selectedIncident, setSelectedIncident] = useState<any>(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const q = query(
      collection(db, 'incidents'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    )
    const unsub = onSnapshot(q, snap => {
      setIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      setLoading(false)
    })
    return () => unsub()
  }, [orgId])

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'incidents', id), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      })
      setShowDetail(false)
      setSelectedIncident(null)
    } catch (e) {
      console.error('Status update error:', e)
    }
  }

  const filtered = incidents.filter(i => {
    if (filterStatus !== 'All' && i.status !== filterStatus) return false
    if (filterSeverity !== 'All' && i.severity !== filterSeverity) return false
    return true
  })

  const formatDate = (iso: string) => {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  if (!hasFeature('incidents')) {
    return (
      <AppLayout title="Incidents">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="bg-gray-100 rounded-full p-6 mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Plan Required</h3>
          <p className="text-gray-500 max-w-sm mb-6">Incident reporting is available on the Enterprise plan.</p>
          <button className="bg-[#e85d26] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#d14d1a]">
            Upgrade to Enterprise
          </button>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Incidents">
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Incident Reports</h3>
            <span className="text-sm text-gray-500">{filtered.length} of {incidents.length}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Status:</span>
              <div className="flex gap-1.5">
                {['All', ...STATUS_ORDER].map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === s
                        ? 'bg-[#1a2332] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Severity:</span>
              <div className="flex gap-1.5">
                {['All', ...SEVERITY_ORDER].map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterSeverity(s)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterSeverity === s
                        ? 'bg-[#1a2332] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Title</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Severity</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Project</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Reported By</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Date</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(incident => (
                    <tr
                      key={incident.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => { setSelectedIncident(incident); setShowDetail(true) }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-1 h-8 rounded-full"
                            style={{ backgroundColor: SEVERITY_COLORS[incident.severity] || '#888' }}
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{incident.title}</p>
                            <p className="text-xs text-gray-500 truncate max-w-xs">{incident.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: (SEVERITY_COLORS[incident.severity] || '#888') + '20',
                            color: SEVERITY_COLORS[incident.severity] || '#888'
                          }}
                        >
                          {incident.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: STATUS_BG[incident.status] || '#f3f4f6',
                            color: STATUS_COLORS[incident.status] || '#6b7280'
                          }}
                        >
                          {incident.status || 'Open'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{incident.projectName || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{incident.reporterName || 'Unknown'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(incident.createdAt)}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedIncident(incident)
                            setShowDetail(true)
                          }}
                          className="text-sm text-[#2563eb] hover:text-[#1d4ed8] font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-gray-400 text-sm">No incidents match the selected filters.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetail && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedIncident.title}</h3>
                <p className="text-sm text-gray-500">Reported on {formatDate(selectedIncident.createdAt)}</p>
              </div>
              <button
                onClick={() => { setShowDetail(false); setSelectedIncident(null) }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex px-3 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: (SEVERITY_COLORS[selectedIncident.severity] || '#888') + '20',
                    color: SEVERITY_COLORS[selectedIncident.severity] || '#888'
                  }}
                >
                  {selectedIncident.severity}
                </span>
                <span
                  className="inline-flex px-3 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: STATUS_BG[selectedIncident.status] || '#f3f4f6',
                    color: STATUS_COLORS[selectedIncident.status] || '#6b7280'
                  }}
                >
                  {selectedIncident.status || 'Open'}
                </span>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Description</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{selectedIncident.description}</p>
              </div>

              {selectedIncident.incidentType && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Incident Type</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.incidentType}</p>
                </div>
              )}

              {selectedIncident.location && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Location</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.location}</p>
                </div>
              )}

              {selectedIncident.locationDescription && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Location Description</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.locationDescription}</p>
                </div>
              )}

              {selectedIncident.injuredPerson && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Injured Person</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.injuredPerson}</p>
                </div>
              )}

              {selectedIncident.actionsTaken && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Actions Taken</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.actionsTaken}</p>
                </div>
              )}

              {selectedIncident.projectName && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Project</h4>
                  <p className="text-sm text-gray-600">{selectedIncident.projectName}</p>
                </div>
              )}

              {selectedIncident.photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Photos</h4>
                  <div className="flex gap-3 flex-wrap">
                    {selectedIncident.photos.map((uri: string, idx: number) => (
                      <img key={idx} src={uri} alt={`Incident photo ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Reported by <span className="font-medium text-gray-700">{selectedIncident.reporterName || 'Unknown'}</span>
                </p>
              </div>

              {isAdmin() && (
                <div className="pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Update Status</h4>
                  <div className="flex gap-2">
                    {STATUS_ORDER.map(status => (
                      <button
                        key={status}
                        onClick={() => handleStatusUpdate(selectedIncident.id, status)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedIncident.status === status
                            ? 'bg-[#1a2332] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
